import 'whatwg-fetch';
import { deleteTokenCookie } from '../utilities';
import { captureMessage } from 'Utils/errorTracking';
import {
  SITE_HOST,
  API_URL,
  PAYMENTS_API_URL,
  LOCALIZED_MESSAGES_URL,
} from 'Settings';

const parseResultJson = (response) => {
  const resultText = response.text();
  return resultText.then((text) => {
    // In addition to JSON, we also allow empty strings
    // when there’s nothing to return.
    try {
      const json = text.length === 0 ? {} : JSON.parse(text);
      return {
        ...json,
        meta: {
          status: response.status,
        },
      };
    } catch (e) {
      return response;
    }
  });
};

const checkForFiveHundred = (response) => {
  if (response.status >= 200 && response.status < 500) {
    return response;
  }
  const error = new Error(response.statusText);
  error.response = response;
  throw error;
};

const handleUnauthorized = (response) => {
  const { status } = response;
  if (status === 401) {
    return deleteTokenCookie(response);
  }

  if (response.errorMsg) {
    const error = new Error(response.errorMsg);
    error.response = response;
    throw error;
  } else {
    return response;
  }
};

const handleError = (error, info) => {
  // For now, let’s try just capturing as a message, since these
  // are almost always API errors.
  const setMessage = (apiError) => {
    switch (apiError.response.errorCode) {
      case 49:
        return 'Free trial: Username already taken';
      default:
        return apiError.response.errorMsg;
    }
  };
  if (typeof error.response !== 'undefined') {
    if (error.response.errorMsg) {
      captureMessage(setMessage(error), {
        email: info && info.email,
        extra: { ...error },
      });
    }
    return error.response;
  }
  if (typeof error.message === 'string') {
    captureMessage(error.message, {
      email: info && info.email,
      extra: { ...error },
    });
    switch (error.message) {
      case 'Failed to fetch':
        return {
          errorCode: 1001,
          errorMsg: 'The server could not process the request.',
        };
      default:
        console.log(error.name, error.message);
        return {
          errorCode: 1000,
          errorMsg: 'The server could not process the request.',
        };
    }
  }
  return error;
};

/* TODO: Clean this up! */
const apiRequestGenerator = (
  url,
  token = null,
  method = 'GET',
  payload = null,
  includeChecksAndCatch = true,
  additionalHeaders = {},
  { ignoreCredentials = false } = {}
) => {
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(!ignoreCredentials && { credentials: 'include' }),
      ...additionalHeaders,
    },
  };
  if (payload) options.body = payload;
  if (token) options.headers.Authorization = `Bearer ${token}`;
  const request = fetch(url, options);
  if (includeChecksAndCatch) {
    return request
      .then(parseResultJson)
      .then(checkForFiveHundred)
      .then(handleUnauthorized)
      .catch((error) =>
        handleError(error, {
          email: JSON.parse(payload) && JSON.parse(payload).username,
        })
      );
  }
  return request;
};

/*
  Auth calls
*/

export const logout = (token) => {
  const url = `${API_URL}/auth/logout`;
  return apiRequestGenerator(url, token, 'POST', null, false)
    .then(checkForFiveHundred)
    .then(deleteTokenCookie)
    .then((response) => response);
};

export const postLogin = (fields) => {
  const url = `${API_URL}/auth/login`;
  const { username, password } = fields;
  return apiRequestGenerator(
    url,
    null,
    'POST',
    JSON.stringify({
      username: username.value,
      password: password.value,
    }),
    true
  );
};

export const postLoginBeforePasswordChange = (fields) => {
  const filteredFields = {
    username: fields.username,
    password: fields.currentPassword,
  };
  return postLogin(filteredFields);
};

export const postStudentLogin = (fields) => {
  const url = `${API_URL}/auth/student-login`;
  const { username, accessCode } = fields;
  return apiRequestGenerator(
    url,
    null,
    'POST',
    JSON.stringify({
      username: username.value,
      school_code: accessCode.value,
    }),
    false
  )
    .then(parseResultJson)
    .then(checkForFiveHundred)
    .then(handleUnauthorized)
    .catch((error) => handleError(error, username.value));
};

export const validateSchoolCode = (fields) => {
  const url = `${API_URL}/auth/school-code/validate/${fields.accessCode.value}`;
  return apiRequestGenerator(url);
};

export const postForgotPassword = (fields) => {
  const url = `${API_URL}/auth/reset-password`;
  const { username } = fields;
  const body = JSON.stringify({
    email: fields.username.value,
    hostname: SITE_HOST,
  });
  return apiRequestGenerator(url, null, 'POST', body, true);
};

export const checkResetPasswordToken = (token) => {
  const url = `${API_URL}/auth/reset-password/validate/${encodeURIComponent(
    token
  )}`;
  return apiRequestGenerator(url);
};

export const resetPassword = (fields, requestArgs, token = null) => {
  const url = `${API_URL}/users/${requestArgs.id}`;
  const body = JSON.stringify({
    auth_info: {
      password: fields.password.value,
    },
  });
  return apiRequestGenerator(url, null, 'PATCH', body, true, {
    'X-Reset-Password-Token': requestArgs.resetPasswordToken,
  });
};

export const changePassword = (fields, requestArgs, token = null) => {
  const url = `${API_URL}/auth/change-password/${requestArgs.id}`;
  const body = JSON.stringify({
    new_password: fields.newPassword.value,
  });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

/*
  Free trial calls
  (There is only one, but it is special so it gets its own section!)
*/

export const postFreeTrial = (fields, requestArgs) => {
  const url = `${API_URL}/free-trial`;
  const body = {
    given_name: fields.given_name.value,
    family_name: fields.family_name.value,
    marketing_email_subscription: fields.tipsTricks.value,
    school_name: fields.schoolName.value,
    email: fields.username.value,
    password: fields.password.value,
    license_type: fields.licenseType.value,
  };
  if (fields.source && fields.source.value.length > 0) {
    const meta = JSON.stringify({
      source: fields.source.value,
    });
    body.school_meta = meta;
  }
  return apiRequestGenerator(url, null, 'POST', JSON.stringify(body), false)
    .then(parseResultJson)
    .then(checkForFiveHundred)
    .then(handleUnauthorized)
    .catch((error) => handleError(error, { email: fields.username.value }));
};

/*
  Super Admin calls
*/

export const getUsersStats = (token) =>
  apiRequestGenerator(`${API_URL}/admin/stats/educators`, token);

export const getSchoolsStats = (token) =>
  apiRequestGenerator(`${API_URL}/admin/stats/schools`, token);

export const getGenericStats = (token, route) =>
  apiRequestGenerator(`${API_URL}${route}`, token);

/*
  User calls
*/

export const patchUser = (fields, requestArgs, token = null) => {
  const url = `${API_URL}/users/${requestArgs.id}`;
  const body = {};
  Object.keys(fields).map((key) => {
    switch (key) {
      case 'username':
        body.username = fields[key].value;
        // body.auth_info = { 'username': fields[key].value }
        break;
      case 'given_name':
        body.given_name = fields[key].value;
        break;
      case 'family_name':
        body.family_name = fields[key].value;
        break;
      default:
        body[key] = fields[key].value;
    }
  });
  return apiRequestGenerator(url, token, 'PATCH', JSON.stringify(body), true);
};

export const patchEmailSubcriptions = (changeset, token = null) => {
  const url = `${API_URL}/users/me/email-subscriptions`;
  return apiRequestGenerator(
    url,
    null,
    'PATCH',
    JSON.stringify(changeset),
    true,
    {
      'X-Email-Subscription-Token': encodeURIComponent(token),
    }
  );
};

export const postDeactivateUser = (schoolId, userId, token = null) => {
  const url = `${API_URL}/schools/${schoolId}/users/${userId}/deactivate`;
  return apiRequestGenerator(url, token, 'POST');
};

export const getUser = (id, token) => {
  const url = `${API_URL}/users/${id}`;
  return apiRequestGenerator(url, token);
};

export const patchInvitedUser = (fields, requestArgs, token = null) => {
  // TODO: Replace with the API endpoint Tom is creating.
  // Right now, it ignores the string after `/users/` so I picked 'new'.
  const url = `${API_URL}/users/new`;
  const body = JSON.stringify({
    given_name: fields.given_name.value,
    family_name: fields.family_name.value,
    marketing_email_subscription: fields.tipsTricks.value,
    account_status: 'active',
    auth_info: {
      username: fields.username.value,
      password: fields.password.value,
    },
  });
  return apiRequestGenerator(url, null, 'PATCH', body, true, {
    'X-Invite-Token': requestArgs.inviteToken,
  });
};

export const setUIHint = (hint, token) => {
  const url = `${API_URL}/users/ui-hints`;
  const body = JSON.stringify({
    hint,
  });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

export const postStatsEvent = (app, action, token) => {
  return apiRequestGenerator(
    `${API_URL}/stats/event`,
    token,
    'POST',
    JSON.stringify({ app, action }),
    true
  );
};

export const checkInviteToken = (token) => {
  const url = `${API_URL}/invites/validate/${encodeURIComponent(token)}`;
  return apiRequestGenerator(url);
};

export const getClassesForUser = (id, token) => {
  const url = `${API_URL}/users/${id}/classes`;
  return apiRequestGenerator(url, token);
};

export const getTeachersForSchool = (id, token) => {
  const url = `${API_URL}/schools/${id}/teachers`;
  return apiRequestGenerator(url, token);
};

export const getStudentsForSchool = (id, token) => {
  const url = `${API_URL}/schools/${id}/students`;
  return apiRequestGenerator(url, token);
};

export const postInviteTeacher = (email, schoolId, token) => {
  const url = `${API_URL}/invites`;
  const body = JSON.stringify({
    email,
    roles: ['TEACHER'],
    school_id: schoolId,
    hostname: SITE_HOST,
  });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

export const deleteInvitedTeacher = (email, schoolId, token) => {
  const url = `${API_URL}/invites`;
  const body = JSON.stringify({
    email,
    school_id: schoolId,
  });
  return apiRequestGenerator(url, token, 'DELETE', body, true);
};

// @TODO: Change the attribute keys from camel to snake case
const addNewStudent = (url, fields, schoolId, token) => {
  const { given_name, family_name, username } = fields;
  const body = JSON.stringify({
    students: [
      {
        givenName: given_name.value,
        familyName: family_name.value,
        roles: [[schoolId, ['STUDENT']]],
        auth_info: {
          username: username.value,
        },
      },
    ],
  });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

export const addNewStudentToSchool = (fields, requestArgs, token) => {
  const url = `${API_URL}/schools/${requestArgs.schoolId}/students`;
  return addNewStudent(url, fields, requestArgs.schoolId, token);
};

/*
  Schools Calls
*/

export const getSchools = (token) => {
  const url = `${API_URL}/schools`;
  return apiRequestGenerator(url, token);
};

export const getSchool = (id, token) => {
  const url = `${API_URL}/schools/${id}`;
  return apiRequestGenerator(url, token);
};

export const getSchoolLicenses = (id, token) =>
  apiRequestGenerator(`${API_URL}/schools/${id}/licenses`, token);

export const getSeatLimitsForSchool = (token, schoolId, userType) => {
  const url = `${API_URL}/schools/${schoolId}/limits/${userType}`;
  return apiRequestGenerator(url, token);
};

export const getSchoolDisabledModules = (token, schoolId) => {
  const url = `${API_URL}/schools/${schoolId}/disabled-modules`;
  return apiRequestGenerator(url, token);
};

export const getUserClassDisabledModules = (token, userId) => {
  const url = `${API_URL}/users/${userId}/disabled-modules`;
  return apiRequestGenerator(url, token);
};

export const postSchoolDisabledModules = (
  token,
  { schoolId, disabledModules }
) => {
  const url = `${API_URL}/schools/${schoolId}/disabled-modules`;
  const body = JSON.stringify({ disabled_modules: disabledModules });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

/*
  Classes calls
*/

export const createClass = (fields, requestArgs, token) => {
  const url = `${API_URL}/classes`;
  const body = JSON.stringify({
    classes: [
      {
        name: fields.className.value,
        teacher_ids: requestArgs.teacher_ids || [],
        student_ids: [],
        school_id: requestArgs.schoolId,
      },
    ],
  });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

export const getClass = (id, token) => {
  const url = `${API_URL}/classes/${id}`;
  return apiRequestGenerator(url, token);
};

export const getClassDisabledModules = (classId, token) => {
  const url = `${API_URL}/classes/${classId}/disabled-modules`;
  return apiRequestGenerator(url, token);
};

export const postClassDisabledModules = (
  token,
  { schoolId, classId, disabledModules }
) => {
  const url = `${API_URL}/classes/${classId}/disabled-modules`;
  const body = JSON.stringify({
    disabled_modules: disabledModules,
    school_id: schoolId,
  });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

export const getStudentsInClass = (id, token) => {
  const url = `${API_URL}/classes/${id}/students`;
  return apiRequestGenerator(url, token);
};

export const addNewStudentToClass = (fields, requestArgs, token) => {
  const url = `${API_URL}/classes/${requestArgs.classId}/students`;
  return addNewStudent(url, fields, requestArgs.schoolId, token);
};

export const addExistingStudentToClass = (studentId, classId, token) => {
  const url = `${API_URL}/classes/${classId}/students`;
  const body = JSON.stringify({
    student_ids: [studentId],
  });
  return apiRequestGenerator(url, token, 'POST', body, true);
};

export const removeStudentFromClass = (studentId, classId, token) => {
  const url = `${API_URL}/classes/${classId}/students`;
  const body = JSON.stringify({
    student_ids: [parseInt(studentId, 10)],
  });
  return apiRequestGenerator(url, token, 'DELETE', body, true);
};

export const createSchoolSubscription = (
  fields,
  { stripeSourceId, authToken, school, user, selectedPlan, hasExpiredLicense }
) => {
  const endpoint = '/schools-subscription';
  const url = `${PAYMENTS_API_URL}${endpoint}`;
  const values = {
    source_token: stripeSourceId,
    customer_email: user.auth_info.username,
    license_type: selectedPlan,
    school_meta: school.meta,
    school_id: school.id,
    user_id: parseInt(user.id, 10),
    school_is_deactivated: hasExpiredLicense,
  };
  Object.keys(fields).map((id) => {
    const f = fields[id];
    if (f.fieldName !== 'credit_card' && f.value.length > 0) {
      values[f.fieldName] = f.value;
    }
  });

  if (school.payment_user_id) values.customer_id = school.payment_user_id;
  const body = JSON.stringify(values);
  return apiRequestGenerator(
    url,
    authToken,
    'POST',
    body,
    true,
    {},
    { ignoreCredentials: true }
  );
};

export const addPaymentMethod = (
  fields,
  { stripeSourceId, schoolId },
  authToken
) => {
  const endpoint = '/add-payment-method';
  const url = `${PAYMENTS_API_URL}${endpoint}`;
  const values = {
    stripe_src_token: stripeSourceId,
    school_id: schoolId,
  };

  const body = JSON.stringify(values);

  return apiRequestGenerator(
    url,
    authToken,
    'POST',
    body,
    true,
    {},
    { ignoreCredentials: true }
  );
};

export const createNewSchoolWithSubscription = (
  source_token,
  auth_token,
  school,
  user,
  license_type,
  fields
) => {
  const endpoint = '/create-paid-school';
  const url = `${PAYMENTS_API_URL}${endpoint}`;
  const values = {
    source_token,
    customer_email: user.auth_info.username,
    license_type,
    school_name: school.name,
    user_given_name: user.given_name,
    user_family_name: user.family_name,
    user_username: user.auth_info.username,
    user_password: user.auth_info.password,
  };

  Object.keys(fields).map((id) => {
    const f = fields[id];
    if (f.fieldName !== 'credit_card' && f.value.length > 0) {
      values[f.fieldName] = f.value;
    }
  });

  if (school.payment_user_id) values.customer_id = school.payment_user_id;
  const body = JSON.stringify(values);
  return apiRequestGenerator(
    url,
    auth_token,
    'POST',
    body,
    true,
    {},
    { ignoreCredentials: true }
  );
};

export const getStripeSubscription = (authToken, schoolId) => {
  const endpoint = `/subscriptions/${schoolId}`;
  const url = `${PAYMENTS_API_URL}${endpoint}`;
  return apiRequestGenerator(
    url,
    authToken,
    'GET',
    null,
    true,
    {},
    { ignoreCredentials: true }
  );
};

export const toggleSubscriptionAutoRenew = (authToken, schoolId) => {
  const endpoint = `/subscriptions/${schoolId}/toggle-auto-renew`;
  const url = `${PAYMENTS_API_URL}${endpoint}`;
  return apiRequestGenerator(
    url,
    authToken,
    'POST',
    null,
    true,
    {},
    { ignoreCredentials: true }
  );
};

export const checkUsernameValidity = (fields) => {
  return apiRequestGenerator(
    `${API_URL}/auth/username/validate/${encodeURIComponent(
      fields.username.value
    )}`
  );
};

export const fetchLanguageStrings = (locale) => {
  const url = `${LOCALIZED_MESSAGES_URL}${locale}.json`;
  return apiRequestGenerator(url, null, 'GET', null, false)
    .then(parseResultJson)
    .then((response) => response);
};
